/**
 * Tracks route changes for ticket diagnostic breadcrumbs.
 * Exports a React component (NavigationTracker) to render
 * inside the Router, and a reader function.
 */

import { useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"

interface NavigationEntry {
  url: string
  timestamp: number
}

const MAX_ENTRIES = 15
const buffer: NavigationEntry[] = []

function pushNavigation(url: string) {
  // Skip duplicate consecutive entries (e.g. re-renders)
  if (buffer.length > 0 && buffer[buffer.length - 1].url === url) return

  buffer.push({ url, timestamp: Date.now() })
  if (buffer.length > MAX_ENTRIES) {
    buffer.shift()
  }
}

export function getNavigationHistory(): NavigationEntry[] {
  return [...buffer]
}

/**
 * Render inside <BrowserRouter> to track route changes.
 * No DOM output.
 */
export function NavigationTracker() {
  const location = useLocation()
  const isFirst = useRef(true)

  useEffect(() => {
    pushNavigation(location.pathname + location.search)
    if (isFirst.current) {
      isFirst.current = false
    }
  }, [location.pathname, location.search])

  return null
}
